<script lang="ts">
	import type { Season } from '#lib/schema-extension.js';
	import type {
		HDScheduleResponseData,
		HDScheduleResponseRow,
		HDScheduleStream
	} from '../types/responses/Schedule.js';
	import type { TableCol } from '../types/util/TableCol.js';
	import Table from '#lib/components/Table.svelte';
	import { GameStatus } from '../types/util/GameStatus.js';
	import dayjs from 'dayjs';
	import { goto } from '$app/navigation';
	import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
	import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
	import Placeholder from '#lib/components/Placeholder.svelte';
	import { getSchedule } from '../hockeydata.remote.js';

	interface HDScheduleProps {
		season?: Season;
		teamId?: string;
		title?: string | null;
	}

	let { season, teamId, title }: HDScheduleProps = $props();

	let schedule = $derived.by(() => {
		if (!season) {
			return Promise.reject('No season provided');
		}
		return getSchedule(season);
	});

	const scheduleCols: TableCol[] = [
		{
			key: 'scheduledDate',
			label: 'Datum',
			sortable: true,
			sortFunction: (a: HDScheduleResponseRow) => a.scheduledDate.sortValue,
			class: 'justify-start'
		},
		{
			key: 'homeTeamName',
			label: 'Heim',
			sortable: true,
			sortFunction: (a: HDScheduleResponseRow) => a.homeTeamShortName,
			class: 'justify-end'
		},
		{
			key: 'result',
			label: ''
		},
		{
			key: 'awayTeamName',
			label: 'Gast',
			sortable: true,
			sortFunction: (a: HDScheduleResponseRow) => a.awayTeamShortName,
			class: 'justify-start'
		},
		{
			key: 'status',
			label: 'Status',
			class: 'justify-start text-start'
		}
	];

	function getGames(schedule: HDScheduleResponseData) {
		return schedule
			? schedule.rows.map((game) => ({
					...game,
					id: game.id,
					highlight: game.homeTeamId.toString() === teamId || game.awayTeamId.toString() === teamId
				}))
			: [];
	}
</script>

{#await schedule}
	<Placeholder text="Spielplan wird geladen" />
{:then scheduleResponse}
	{@const schedule = scheduleResponse.data}
	<Table
		columns={scheduleCols}
		rows={getGames(schedule)}
		{title}
		onSelect={(row) => goto(`/match/${row.id}`)}
	>
		{#if schedule?.rows.length === 0}
			<div class="text-primary text-center">Keine Spiele gefunden</div>
		{/if}
		{#snippet data(row, col, getRowData)}
			{#if col.key === 'status'}
				<div class="flex flex-row items-center justify-start gap-2">
					{#if row.seasonPhase.includes('PO')}
						<span
							class="text-primary ml-2 border-2 border-yellow-500/50 px-1 text-sm font-semibold uppercase"
						>
							PLAYOFFS
						</span>
					{/if}
					{#if row.gameStatus === GameStatus.PLANNED}
						<span
							class="text-primary ml-2 w-[12ch] border-2 border-blue-500/50 px-2 py-1 text-sm font-semibold uppercase"
						>
							GEPLANT
						</span>
					{:else if row.gameStatus === GameStatus.LIVE}
						<span
							class="text-primary ml-2 w-[12ch] border-2 border-red-500/50 px-2 py-1 text-sm font-semibold uppercase"
						>
							{row.liveTimeString}
						</span>
					{:else if row.gameStatus >= GameStatus.ENDED}
						<span
							class="text-primary ml-2 w-[12ch] border-2 border-green-500/50 px-1 text-sm font-semibold uppercase"
						>
							END{row.isOvertime ? ' OT' : ''}
							{row.isShootOut ? ' PS' : ''}
						</span>
					{/if}
					{#if row.streams.length > 0}
						{#each row.streams as stream}
							{@const streamData = JSON.parse(stream) as HDScheduleStream}
							<a
								href={streamData.targetUrl}
								target={streamData.target}
								rel="noopener noreferrer"
								class="text-base-content hover:bg-base-200 inline-flex min-h-11 items-center gap-1 border-2 border-red-500/50 px-2 text-sm font-semibold uppercase hover:bg-red-500/50"
								onclick={(e) => {
									e.stopPropagation();
								}}
							>
								{streamData.label || 'Livestream'}
								<FontAwesomeIcon icon={faExternalLinkAlt} class="ml-1 h-3 w-3" />
							</a>
						{/each}
					{/if}
				</div>
			{:else if col.key === 'scheduledDate'}
				<div class="flex flex-col items-start">
					<div>
						{dayjs(row.scheduledDate.sortValue).format('DD.MM.YYYY')}
					</div>
					<div class="text-xs">
						{row.scheduledTime}
					</div>
				</div>
			{:else if col.key === 'homeTeamName'}
				<div class="flex flex-row items-center justify-end gap-2">
					<img
						src={`https://api.hockeydata.net/img/icehockey/ebel/team-logos/${season?.seasonId}/${row.homeTeamId}.png`}
						alt={row.homeTeamShortName}
						class="hidden h-8 w-8 md:block"
					/>
					<div class="w-[6ch] text-right">{row.homeTeamShortName}</div>
				</div>
			{:else if col.key === 'result'}
				{#if row.gameStatus === GameStatus.PLANNED}
					<div>vs</div>
				{:else}
					<div>{row.homeTeamScore}&nbsp;:&nbsp;{row.awayTeamScore}</div>
				{/if}
			{:else if col.key === 'awayTeamName'}
				<div class="flex flex-row items-center gap-2">
					<div class="w-[6ch] text-left">{row.awayTeamShortName}</div>
					<img
						src={`https://api.hockeydata.net/img/icehockey/ebel/team-logos/${season?.seasonId}/${row.awayTeamId}.png`}
						alt={row.awayTeamShortName}
						class="hidden h-8 w-8 md:block"
					/>
				</div>
			{:else}
				{getRowData(row, col.key)}
			{/if}
		{/snippet}
	</Table>
{:catch error}
	{error}
{/await}
